import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Search, FolderOpen, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 24;

/**
 * PaisleyPhotoPanel — Brian's own photographs, read from the Cloudflare R2
 * bucket behind media.paisley.org.uk. Folders are the useful handle here
 * (Paisley Abbey, Drone, Town Hall and so on), because a good number of the
 * filenames are camera output with nothing to search on.
 */
export const PaisleyPhotoPanel = ({ onUse }) => {
  const [photos, setPhotos] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [total, setTotal] = useState(0);
  const [hidden, setHidden] = useState(0);
  const [described, setDescribed] = useState(0);
  const [library, setLibrary] = useState(0);
  const [describing, setDescribing] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const request = React.useRef(0);

  // Which photos get picked is what decides which get described first, so each
  // choice is noted. Nothing here should block the editor, hence the catch.
  const pick = (photo) => {
    const token = localStorage.getItem('admin_token');
    axios.post(`${BACKEND_URL}/api/admin/photos/used`, null, {
      params: { key: photo.key },
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    onUse(photo.url, photo);
  };

  // Describing costs a little per photo, so it is only ever started by hand and
  // in a capped batch. The search is reloaded when it finishes.
  const describeMore = async () => {
    setDescribing(true);
    const token = localStorage.getItem('admin_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${BACKEND_URL}/api/admin/photos/jobs/caption/start`, null, {
        params: { scope: 'library', limit: 100 }, headers,
      });
      for (let i = 0; i < 60; i += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        const res = await axios.get(`${BACKEND_URL}/api/admin/photos/jobs`, { headers });
        setDescribed(res.data?.photos?.with_alt_text || described);
        if (res.data?.caption?.state !== 'running') break;
      }
      await load(0, q, folder);
    } catch {
      /* the count simply stays as it was */
    } finally {
      setDescribing(false);
    }
  };

  const load = useCallback(async (nextSkip = 0, nextQ = q, nextFolder = folder) => {
    const ticket = ++request.current;
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/photos`, {
        params: { q: nextQ, folder: nextFolder, limit: PAGE, skip: nextSkip,
                  include_hidden: showHidden },
        headers: { Authorization: `Bearer ${token}` },
      });
      // Two searches can be in flight at once; only the newest may paint.
      if (ticket !== request.current) return;
      const rows = res.data.photos || [];
      setPhotos((prev) => (nextSkip === 0 ? rows : [...prev, ...rows]));
      setFolders(res.data.folders || []);
      setTotal(res.data.total || 0);
      setHidden(res.data.hidden || 0);
      setDescribed(res.data.described || 0);
      setLibrary(res.data.library || 0);
      setSkip(nextSkip);
    } catch {
      if (ticket === request.current) setPhotos([]);
    } finally {
      if (ticket === request.current) setLoading(false);
    }
  }, [q, folder, showHidden]);

  useEffect(() => {
    const t = setTimeout(() => load(0, q, folder), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, folder, showHidden]);

  return (
    <div className="flex flex-col h-full" data-testid="paisley-photo-panel">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100 bg-slate-50">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by what's in the photo — 'snow', 'bandstand', 'fans'"
            className="pl-9"
            data-testid="photo-search-input"
          />
        </div>
        <select
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"
          data-testid="photo-folder-select"
        >
          <option value="">All folders</option>
          {folders.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <span className="text-xs text-slate-500" data-testid="photo-count">
          {total.toLocaleString()} photo{total === 1 ? '' : 's'}
        </span>
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            className="text-xs text-slate-500 underline decoration-dotted hover:text-cyan-700"
            title="Duplicates, old WordPress crops and .bak copies, kept in the bucket but out of your way"
            data-testid="photo-hidden-toggle"
          >
            {showHidden ? 'hide' : 'show'} {hidden.toLocaleString()} duplicates
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {q && described < library && (
          <p className="text-xs text-slate-500 pb-3" data-testid="photo-described-note">
            Searching names and descriptions. {described.toLocaleString()} of{' '}
            {library.toLocaleString()} photos have been described so far —
            {' '}
            <button
              type="button"
              onClick={describeMore}
              disabled={describing}
              className="underline decoration-dotted hover:text-cyan-700 disabled:opacity-60"
              data-testid="photo-describe-more"
            >
              {describing ? 'describing…' : 'describe more'}
            </button>
          </p>
        )}
        {photos.length === 0 && !loading && (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="photo-empty">
            Nothing matches that. The undescribed photos can only be found by filename or
            folder yet, so try a folder — or describe more and search again.
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => pick(p)}
              className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200
                         hover:border-cyan-500 hover:ring-2 hover:ring-cyan-200 transition-all"
              title={p.alt ? `${p.alt}\n\n${p.key}` : p.key}
              data-testid={`photo-${p.key}`}
            >
              <img
                src={p.thumb || p.url}
                alt={p.alt || p.filename}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <span className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[10px]
                               px-1.5 py-1 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="block truncate">
                  {p.folder ? `${p.folder} · ` : ''}{p.filename}
                </span>
                {p.alt && <span className="block line-clamp-2 text-white/75">{p.alt}</span>}
              </span>
              {p.hidden_reason && (
                <span className="absolute top-1 left-1 bg-slate-700 text-white text-[9px]
                                 px-1.5 py-0.5 rounded">
                  {p.hidden_reason}
                </span>
              )}
              {p.size > 3000000 && !(p.thumb || '').includes('_thumbs') && (
                <span className="absolute top-1 right-1 bg-amber-500 text-white text-[9px]
                                 font-bold px-1.5 py-0.5 rounded"
                      title="Original not yet resized — slow to load on mobile">
                  {(p.size / 1048576).toFixed(0)}MB
                </span>
              )}
            </button>
          ))}
        </div>

        {photos.length < total && (
          <div className="text-center pt-4">
            <button
              type="button"
              onClick={() => load(skip + PAGE)}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border
                         border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              data-testid="photo-load-more"
            >
              {loading ? <Loader2 size={14} className="animate-spin inline" /> : <FolderOpen size={14} className="inline mr-1.5" />}
              Show more
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaisleyPhotoPanel;
